/**
 * Asset library drawer — right-side panel listing every committed
 * image for the current campaign, with a file picker for uploads and
 * per-row delete. Uploads go through `uploadAsset` which runs the
 * full presign → S3 PUT → commit flow.
 *
 * This component owns its own fetch + cache for now; when image
 * elements land on the canvas (Stage G) this cache will become the
 * source of truth for the drag-to-canvas flow.
 *
 * The library is scoped per campaign. Until the dashboard wires a
 * real campaign picker we reuse the same placeholder the scene
 * sync uses ("local-dev") so uploaded assets persist across sessions.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { deleteAsset, listAssets, uploadAsset, type UploadProgress } from "../../lib/assets";
import { ALLOWED_ASSET_CONTENT_TYPES, type AssetWithUrl } from "../../lib/assetsTypes";
import { imageCache } from "../render/imageCache";
import "./AssetLibrary.css";

/**
 * Drag payload carried from a library thumbnail to the canvas. Must be
 * kept in sync with the matching parser in SceneCanvas.
 */
export const ASSET_DRAG_MIME = "application/x-goobdoob-asset";
export interface AssetDragPayload {
  fileId: string;
  filename: string;
  naturalWidth: number;
  naturalHeight: number;
  downloadUrl: string;
}

interface AssetLibraryProps {
  campaignId: string;
}

export default function AssetLibrary({ campaignId }: AssetLibraryProps) {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState<AssetWithUrl[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listAssets(campaignId);
      // Newest first — server doesn't sort, library is small enough
      // that sorting client-side is cheaper than a GSI.
      const sorted = [...resp.assets].sort(
        (a, b) => b.record.createdAt - a.record.createdAt,
      );
      setAssets(sorted);
      // Pre-warm the render cache so dropped images paint instantly
      // instead of blinking through a placeholder frame.
      for (const a of sorted) imageCache.preload(a.record.fileId, a.downloadUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  const onPick = () => fileInputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same filename
    if (!file) return;
    setError(null);
    setUploadState({ phase: "presign" });
    try {
      const uploaded = await uploadAsset(campaignId, file, (p) => setUploadState(p));
      imageCache.preload(uploaded.record.fileId, uploaded.downloadUrl);
      setAssets((prev) => [uploaded, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadState(null);
    }
  };

  const onDelete = async (fileId: string) => {
    // No confirmation dialog — the library is private per campaign and
    // mis-deletes are cheap to recover from by re-uploading. If this
    // ever causes pain we'll add a confirm.
    try {
      await deleteAsset(campaignId, fileId);
      imageCache.invalidate(fileId);
      setAssets((prev) => prev.filter((a) => a.record.fileId !== fileId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <button
        type="button"
        className={`asset-library__toggle${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title="Asset library"
      >
        🖼 Assets
      </button>
      {open && (
        <aside className="asset-library">
          <header className="asset-library__header">
            <h3>Asset library</h3>
            <button type="button" className="asset-library__close" onClick={() => setOpen(false)}>
              ×
            </button>
          </header>

          <div className="asset-library__actions">
            <button
              type="button"
              className="asset-library__upload"
              onClick={onPick}
              disabled={uploadState !== null}
            >
              {uploadState ? uploadLabel(uploadState) : "Upload image"}
            </button>
            <button type="button" className="asset-library__refresh" onClick={refresh} disabled={loading}>
              {loading ? "…" : "↻"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_ASSET_CONTENT_TYPES.join(",")}
              onChange={onFileChange}
              hidden
            />
          </div>

          {error && <div className="asset-library__error">{error}</div>}

          <div className="asset-library__grid">
            {assets.map((a) => (
              <figure
                key={a.record.fileId}
                className="asset-library__item"
                draggable
                onDragStart={(e) => {
                  const payload: AssetDragPayload = {
                    fileId: a.record.fileId,
                    filename: a.record.filename,
                    naturalWidth: a.record.naturalWidth,
                    naturalHeight: a.record.naturalHeight,
                    downloadUrl: a.downloadUrl,
                  };
                  e.dataTransfer.setData(ASSET_DRAG_MIME, JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = "copy";
                }}
              >
                <img src={a.downloadUrl} alt={a.record.filename} loading="lazy" />
                <figcaption title={a.record.filename}>{a.record.filename}</figcaption>
                <button
                  type="button"
                  className="asset-library__delete"
                  onClick={() => onDelete(a.record.fileId)}
                  title="Delete"
                >
                  ×
                </button>
              </figure>
            ))}
            {!loading && assets.length === 0 && (
              <div className="asset-library__empty">No assets yet. Upload one to get started.</div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}

function uploadLabel(p: UploadProgress): string {
  switch (p.phase) {
    case "presign":
      return "Preparing…";
    case "upload":
      return "Uploading…";
    case "commit":
      return "Finalising…";
  }
}
